import { Component, OnDestroy, OnInit, inject, input, model, output } from '@angular/core';
import { outputFromObservable } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { FormlyFieldConfig, FormlyFormOptions, FormlyModule } from '@ngx-formly/core';
import { Observable, Subject, of } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { v4 as newGuid } from 'uuid';

import {
  BaseEditCreateService,
  BaseEditService,
  ModalService,
  NotificationService,
  RouterService,
} from '../../services';
import { FormService } from '../../services/component-services/form.service';
import { MockEditService } from '../../mock';
import { BaseComponent } from '../base/base.component';

@Component({
  selector: 'app-base-edit-form',
  templateUrl: './base-edit-form.component.html',
  styleUrl: './base-edit-form.component.scss',
  imports: [FormlyModule],
})
export class BaseEditFormComponent extends BaseComponent implements OnInit, OnDestroy {
  private readonly formService = inject(FormService);
  private readonly notificationService = inject(NotificationService);
  private readonly routerService = inject(RouterService);
  private readonly route = inject(ActivatedRoute);
  private readonly modalService = inject(ModalService);

  public readonly fields = input<FormlyFieldConfig[]>();
  public readonly baseService = input<BaseEditService<any, any> | BaseEditCreateService<any, any>>(
    new MockEditService(),
  );
  public readonly updateModelOnSave = input(false);
  /**
   * Set when the caller supplies the record through [model] and the form must NOT fetch its own.
   * getModel() loads by the ROUTE id, which is only the right key when the form edits the entity
   * the route is for - a nested form on someone else's route would otherwise request its service
   * with a foreign id (the currency tabs were doing exactly that, 404ing on every render).
   */
  public readonly skipModelLoad = input(false);
  public readonly closeOnSave = input(true);
  public readonly closeOnCreate = input(false);
  public readonly alwaysSave = input(false);
  public readonly id = input<string>();
  public readonly parentId = input<string>();
  public readonly parentId2 = input<string>();
  public readonly model = model<any>({});
  public readonly modelLoaded = output<any>();
  public readonly modelUpdated = output<any>();
  public readonly saved = output<any>();
  // Emits the HTTP error when the save request fails, so a screen can act on a structured
  // error body (e.g. an overridable warning it re-posts after a Yes/No prompt). When observed,
  // the generic save-failed toast is suppressed and the handler owns the messaging.
  private readonly saveFailed$ = new Subject<HttpErrorResponse>();
  public readonly saveFailed = outputFromObservable(this.saveFailed$);

  public form: UntypedFormGroup = new UntypedFormGroup({});
  protected options: FormlyFormOptions = {};

  private modalLevel: number;
  private formId: string;
  private updatesInProgress = 0;
  private shouldSave = false;

  ngOnInit() {
    this.modalLevel = this.modalService.modalLevel;
    this.getModel();
    this.formService.save.pipe(takeUntil(this.destroy$)).subscribe((modalLevel: number) => {
      if (modalLevel === this.modalLevel) {
        this.onSave();
      }
    });
    this.formId = newGuid();
    this.formService.addForm(this.modalLevel, this.formId, this.form);
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.formService.removeForm(this.modalLevel, this.formId);
  }

  get isModal() {
    return this.modalLevel > 0;
  }

  get isDirty() {
    return this.form?.dirty ?? false;
  }

  get observableModel(): Observable<any> {
    return of(this.model());
  }

  getModel() {
    if (this.skipModelLoad()) {
      return;
    }
    const id = this.isModal ? this.id() : this.route.snapshot.paramMap.get('id');
    (id ? this.baseService().edit(id) : this.baseService().create(this.parentId(), this.parentId2()))
      .pipe(takeUntil(this.destroy$))
      .subscribe((model: any) => {
        this.updateModel(model, false);
        this.modelLoaded.emit(model);
      });
  }

  onSave() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.formService.scrollToFirstInvalidField();
      return;
    }

    if (!this.form.dirty && !this.alwaysSave()) {
      setTimeout(() => {
        if (!this.formService.anySavesInProgress) {
          this.navigate(false);
        }
      }, 0);
      return;
    }

    if (this.updatesInProgress > 0) {
      this.shouldSave = true;
      return;
    }

    this.formService.beginSave();
    this.baseService()
      .save(this.model())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (id: string) => {
          this.notificationService.saveSuccess(this.baseService().name);

          if (this.updateModelOnSave()) {
            this.getModel();
          }

          this.formService.saveComplete.pipe(takeUntil(this.destroy$)).subscribe(() => this.navigate(true, id));

          this.formService.endSave(false);
        },
        error: (error: HttpErrorResponse) => {
          if (this.saveFailed$.observed) {
            this.saveFailed$.next(error);
          } else {
            this.notificationService.saveFail(this.baseService().name);
          }
          this.formService.endSave(true);
        },
      });
  }

  beginUpdate() {
    ++this.updatesInProgress;
  }

  endUpdate() {
    if (--this.updatesInProgress == 0 && this.shouldSave) {
      this.shouldSave = false;
      this.onSave();
    }
  }

  navigate(saved: boolean, id?: string) {
    if (this.isModal) {
      // consumeKeepOpen: the header's Previous / Next asked for "save, then move to the other
      // line", so this one save leaves the modal open for the host to swap the record.
      if (this.closeOnSave() && !this.formService.consumeKeepOpen(this.modalLevel)) {
        this.modalService.close(saved ? this.model() : undefined);
      }
    } else {
      if (this.closeOnSave()) {
        this.routerService.back(this.route);
      } else {
        if (this.formService.isNewModel(this.model()) && !this.closeOnCreate()) {
          if (id) {
            this.routerService.edit(id, this.route);
          }
        }
      }
    }
    if (id) {
      this.model.set({ ...this.model(), id });
    }
    this.options.resetModel();
    this.formService.resetForms(this.modalLevel);
    if (saved) {
      this.saved.emit(this.model());
    }
  }

  updateModel(model: any, markUpdated = true, markUnchanged = false) {
    this.model.set({ ...this.model(), ...model });
    this.modelUpdated.emit(this.model());
    if (markUpdated) {
      this.form.markAsDirty();
      this.form.markAllAsTouched();
    }
    if (markUnchanged) {
      this.form.markAsPristine();
      this.form.markAsUntouched();
    }
  }
}
